import React, { useState, useEffect } from 'react';
import { Card, CardContent, Stack, useTheme, useMediaQuery, Popover, Box, Typography, Link, List, ListSubheader, ListItem, Alert, Table, TableContainer, Paper, TableHead, TableBody, TableRow, TableCell, Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IndependentExpenditureTable from './IndependentExpenditureTable';
import { Description } from '@mui/icons-material';


const IEPACommittee = (): JSX.Element => {
    const theme = useTheme();
    const isSM = useMediaQuery(theme.breakpoints.down('sm'));

    const pacReports = [
        {
            report: 'Initial Report',
            beginReportingPeriod: 'IEPAC Organization date',
            endReportingPeriod: 'Date of the last expenditure disclosed',
            dueDate: 'Within 7 business days of utilization, or within 24 hours if utilized after the 10th day but more than 24 hours before an election',
            description : 'The reporting period for the first report filed by an IE PAC commences on the day the committee was organized and is complete through the date the first independent expenditure was utilized. The first report discloses all monetary and in-kind contributions received during the reporting period, as well as independent expenditures.'
        },
        {
            report: 'Subsequent Reports',
            beginReportingPeriod: 'Following the ending date of the last report filed',
            endReportingPeriod: 'Date of the last expenditure disclosed',
            dueDate: isSM ? 'N/A' : '-',
            description : 'The reporting period for the next report commences on the date following immediately following the last date of the previous report and is complete through the date that the next independent expenditure is utilized. This report discloses all monetary and in-kind contributions received during the reporting period, as well as independent expenditures.'
        },
        {
            report: 'Year-End Report',
            beginReportingPeriod: 'January 1st',
            endReportingPeriod: 'December 31st',
            dueDate: 'January 20th of the following year',
            description : 'All IE PACs file a year-end report on or before January 20 each year. The reporting period is January 1 through December 31. This report discloses the aggregate amount of all previously reported activity for the calendar year and also discloses administrative expenses, subvendor expenditures, and any other financial activity that was not previously disclosed on a 7-Day or 24-Hour report.'
        },
    ];

    return (
        <Box>
            <Typography variant="h6" gutterBottom fontWeight={600}>
                Independent Expenditure Political Action Committees (IE PACs)
            </Typography>
            <Typography variant="body1" component={'p'} gutterBottom>
                An independent expenditure is an expenditure made or liability incurred by an individual, group, association, corporation, labor union, political committee or other entity as payment for goods or services to expressly advocate the election or defeat of a clearly identified candidate; provided, however, that the expenditure is made or incurred without cooperation or consultation with any candidate or a nonelected political committee organized on behalf of the candidate or an agent of the candidate and is not made or incurred in concert with or at the request or suggestion of the candidate, a nonelected political committee organized on behalf of the candidate or agent of the candidate
            </Typography>
            <Typography variant="body1" component={'p'} gutterBottom>
                Entities that intend to raise and spend funds solely for the purpose of making independent expenditures supporting or opposing candidates must organize as an IE PAC. See <Link href='https://ocpf2.blob.core.windows.net/pdf/legaldocs/IB-10-03.pdf' target='_blank'>IB-10-03</Link> and this <Link href='/PublicSearch/ViewDocument?id=3013' target='_blank'>campaign finance guide</Link>.
            </Typography>
            <Typography variant="body1" component={'p'} marginTop={2} gutterBottom>
                IE PACs that support or oppose <b>candidates who file with OCPF</b> must organize with OCPF. Click <Link underline="none" href="https://www.ocpfreporter.us/OnlineOrganization/" target="_blank">here</Link> to register with OCPF.
            </Typography>
            <Typography variant="body1" component={'p'} marginTop={2} gutterBottom>
                IE PACs that support or oppose municipal candidates (non-OCPF filers) must organize with local election officials by filing this <Link href='https://ocpf2.blob.core.windows.net/pdf/forms/m101iepac.pdf?t=Eby0O5F' target='_blank'>form</Link>. Municipal IE PACs also file their <Link href='https://ocpf2.blob.core.windows.net/pdf/forms/cpf_m102_iepac.pdf?t=Eby0O5F' target='_blank'>campaign finance reports</Link> with local election officials.
            </Typography>
            <Accordion sx={{ marginBottom: 3 }} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" marginBottom={1}><b>Filing Schedules for Independent Expenditures made by IEPACs</b></Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <IndependentExpenditureTable />
                </AccordionDetails>
            </Accordion>


            <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" marginBottom={1}><b>Filing Schedule for IEPACs</b></Typography>
                </AccordionSummary>
                <AccordionDetails>

                    {isSM ?
                        pacReports.map((schedule, index) => (
                            <Card variant="outlined" key={index} sx={{ marginBottom: 2 }}>
                                <CardContent>
                                    <Stack spacing={1}>
                                        <Typography variant="body2">
                                            <b>Report: </b>{schedule.report}
                                        </Typography>
                                        <Typography variant="body2">
                                            <b>Description: </b>{schedule.description}
                                        </Typography>
                                        <Typography variant="body2">
                                            <b>Begin Reporting Period: </b>{schedule.beginReportingPeriod}
                                        </Typography>
                                        <Typography variant="body2">
                                            <b>End Reporting Period: </b>{schedule.endReportingPeriod}
                                        </Typography>
                                        <Typography variant="body2">
                                            <b>Date Due: </b>{schedule.dueDate}
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))

                        :
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align='center'><b>Report</b></TableCell>
                                        <TableCell align='center'><b>Description</b></TableCell>
                                        <TableCell align='center'><b>Begin Reporting Period</b></TableCell>
                                        <TableCell align='center'><b>End Reporting Period</b></TableCell>
                                        <TableCell align='center'><b>Date Due</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pacReports.map((report, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <Typography variant='body2' color="primary">
                                                    {report.report}
                                                </Typography>
                                            </TableCell>
                                            <TableCell >{report.description}</TableCell>
                                            <TableCell align='center'>{report.beginReportingPeriod}</TableCell>
                                            <TableCell align='center'>{report.endReportingPeriod}</TableCell>
                                            <TableCell align='center'>{report.dueDate}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    }

                </AccordionDetails>
            </Accordion>



        </Box>





    )
}


export default IEPACommittee;